import TotalReportHead from "@presentation/components/reports/TotalReportsHead";
import ReportRow from "@presentation/components/reports/TotalReportsRow";
import { ReportHeadCellData } from "@domain/Interfaces/HeadCellData.d";
import Order from "@domain/Interfaces/Order.d";
import { Box, TableContainer, Table, TableBody, TableProps } from "@mui/material";
import TotalTablePagination from "@presentation/components/TotalTablePagination";
import React, { useEffect, useState } from "react";
import { ReportHeadCell } from "@domain/Interfaces/HeadCell.d";
import { Report } from "@domain/entities/Reports";
import { Dataset } from "@domain/entities/Roles";
import { User } from "@domain/entities/Users";
import {
  stableSort,
  getComparator,
  getPageNumber,
  calculateNumberOfEmptyRows,
  defaultTableSx,
} from "@presentation/components/table/utils";
import EmptyRows from "@presentation/components/table/EmptyRow";
import { StoredPagination } from "@presentation/utils/StoredPagination";
import useStoredRowsPerPage from "@presentation/utils/StoredRowsPerPage";

interface ITotalReportsTable extends TableProps {
  headCells: ReportHeadCell[];
  rows: Report[];
  datasets: Dataset[];
  users: User[];
  storedPagination: StoredPagination;
  setStoredPagination: (pagination: StoredPagination) => void;
}

const TotalReportsTable = ({
  headCells,
  rows,
  datasets,
  users,
  storedPagination,
  setStoredPagination,
}: ITotalReportsTable): React.JSX.Element => {
  const [rowsPerPage, setRowsPerPage] = useStoredRowsPerPage(10);
  const [pageNumber, setPageNumber] = useState<number>(getPageNumber(rowsPerPage, rows.length));
  if (storedPagination.page > pageNumber) {
    setStoredPagination({ ...storedPagination, page: 0 });
  }

  const handleRequestSort = (_event: React.MouseEvent<unknown>, property: keyof ReportHeadCellData): void => {
    const isAsc = storedPagination.orderBy === property && storedPagination.order === "asc";
    setStoredPagination({ ...storedPagination, order: isAsc ? "desc" : "asc", orderBy: property });
  };

  const handleChangePage = (_event: unknown, newPage: number): void => {
    setStoredPagination({ ...storedPagination, page: newPage - 1 });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRowsPerPage(parseInt(event.target.value));
    setStoredPagination({ ...storedPagination, page: 0 });
  };

  useEffect(() => {
    setPageNumber(getPageNumber(rowsPerPage, rows.length));
  }, [rowsPerPage, rows]);

  const emptyRows = calculateNumberOfEmptyRows(storedPagination.page, rowsPerPage, rows.length);

  return (
    <Box sx={{ width: "100%" }}>
      <TableContainer>
        <Table sx={defaultTableSx} aria-labelledby="tableTitle">
          <TotalReportHead
            order={storedPagination.order as Order}
            orderBy={storedPagination.orderBy as keyof ReportHeadCellData}
            onRequestSort={handleRequestSort}
            headCells={headCells}
          />
          <TableBody>
            {stableSort(
              rows,
              getComparator(storedPagination.order as Order, storedPagination.orderBy as keyof ReportHeadCellData)
            )
              .slice(storedPagination.page * rowsPerPage, storedPagination.page * rowsPerPage + rowsPerPage)
              .map((row, index) => {
                const labelId = `enhanced-table-checkbox-${index}`;
                return (
                  <ReportRow
                    key={`report-list-${row.id}`}
                    apiReport={row}
                    datasets={datasets}
                    users={users}
                    labelId={labelId}
                    highlight={row.datasetId === 0}
                  />
                );
              })}
            <EmptyRows emptyNumber={emptyRows} />
          </TableBody>
        </Table>
      </TableContainer>
      <TotalTablePagination
        page={storedPagination.page}
        pageNumber={pageNumber}
        rowsPerPage={rowsPerPage}
        nbItems={rows.length}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
};

export default TotalReportsTable;
